import type { Cart, DeliveryGroup } from '@/modules/cart/domain/Cart';

import type { InitialState, OnBehalf } from './interfaces';

export const extractOnBehalfDetails = (cart: Cart): OnBehalf[] => {
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups ?? [];
  if (!deliveryGroups) return [];

  const deliveryGroupsWithOnBehalf = deliveryGroups?.filter(
    (deliveryGroup) =>
      deliveryGroup?.collectOnBehalfRut !== undefined ||
      (deliveryGroup?.collectOnBehalfDocumentType !== undefined &&
        deliveryGroup?.collectOnBehalfDocumentID !== undefined)
  );

  return deliveryGroupsWithOnBehalf?.map((deliveryGroup) => {
    const response: OnBehalf = {
      deliveryGroupId: deliveryGroup?.id ?? '',
      lineItemSkus: deliveryGroup?.products ?? [],
      name: deliveryGroup?.collectOnBehalfName ?? '',
      rut: deliveryGroup?.collectOnBehalfRut ?? '',
      documentType: deliveryGroup?.collectOnBehalfDocumentType,
      documentID: deliveryGroup?.collectOnBehalfDocumentID
    };
    return response;
  });
};

export const hasCollectShippingOption = (cart: Cart): boolean => {
  const deliveryGroups = cart?.deliveryInfo?.deliveryGroups;
  if (!deliveryGroups) return false;

  const hasCollectDelivery = deliveryGroups?.some(
    (group) => group?.shippingOption?.shippingType === 'COLLECT'
  );

  return hasCollectDelivery;
};

const matchDeliveryGroupBySkus = (
  deliveryGroups?: DeliveryGroup[],
  onBehalf?: OnBehalf
): DeliveryGroup | undefined => {
  if (!deliveryGroups || !onBehalf) return;

  const onBehalfSkus = Array.from(onBehalf?.lineItemSkus ?? []);
  const deliveryGroup = deliveryGroups?.find((newGroup) => {
    const newProducts = newGroup?.products;
    const hasSameLength = newProducts?.length === onBehalfSkus?.length;
    const isCollect = newGroup?.shippingOption?.shippingType === 'COLLECT';
    const hasSameProducts = onBehalfSkus?.every((sku) =>
      newProducts?.includes(sku)
    );
    return hasSameLength && hasSameProducts && isCollect;
  });

  return deliveryGroup;
};

export const refreshOnBehalfGroups = (
  currentOnBehalf: InitialState['onBehalf'],
  newDeliveryGroups: DeliveryGroup[]
): OnBehalf[] => {
  const newOnBehalf: OnBehalf[] = [];

  currentOnBehalf.forEach((onBehalf) => {
    const deliveryGroup = matchDeliveryGroupBySkus(newDeliveryGroups, onBehalf);
    if (!deliveryGroup) return;

    const newGroup: OnBehalf = {
      deliveryGroupId: deliveryGroup?.id ?? '',
      lineItemSkus: deliveryGroup?.products ?? [],
      name: onBehalf?.name ?? '',
      rut: onBehalf?.rut ?? ''
    };
    newOnBehalf.push(newGroup);
  });

  return newOnBehalf;
};
